import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg, setToken, getToken } from "@/lib/api";
import { track } from "@/lib/track";
import { enablePush, disablePush, playDing, pushSupported } from "@/lib/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = auth
  const [authOpen, setAuthOpen] = useState(false);
  const [badges, setBadges] = useState({ notifications: 0, messages: 0, friend_requests: 0 });

  useEffect(() => {
    if (!getToken()) { setUser(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { setToken(null); setUser(false); });
  }, []);

  const lastTotal = useRef(null);
  const refreshBadges = useCallback(() => {
    if (!getToken()) return;
    api.get("/me/badges").then((r) => {
      const d = r.data;
      const total = (d.notifications || 0) + (d.messages || 0) + (d.friend_requests || 0);
      if (lastTotal.current !== null && total > lastTotal.current && document.visibilityState === "visible") playDing();
      lastTotal.current = total;
      setBadges(d);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || !pushSupported()) return;
    const sync = () => { if (Notification.permission === "granted") enablePush(false).catch(() => {}); };
    sync();
    const autoAsk = () => {
      document.removeEventListener("pointerdown", autoAsk, true);
      if (Notification.permission === "default") enablePush(true).catch(() => {});
    };
    if (Notification.permission === "default") {
      enablePush(true).catch(() => {});
      document.addEventListener("pointerdown", autoAsk, true);
    }
    const onVis = () => document.visibilityState === "visible" && sync();
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); document.removeEventListener("pointerdown", autoAsk, true); };
  }, [user]);

  useEffect(() => {
    if (!(user && user !== false)) { lastTotal.current = null; setBadges({ notifications: 0, messages: 0, friend_requests: 0 }); return; }
    refreshBadges();
    api.post("/presence/ping").catch(() => {});
    const b = setInterval(refreshBadges, 5000);
    const p = setInterval(() => api.post("/presence/ping").catch(() => {}), 30000);
    const onFocus = () => { refreshBadges(); api.post("/presence/ping").catch(() => {}); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(b); clearInterval(p); window.removeEventListener("focus", onFocus); };
  }, [user, refreshBadges]);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (r.data.token) setToken(r.data.token);
    setUser(r.data.user);
    track("login");
    return r.data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await api.post("/auth/register", { ...payload, ref: localStorage.getItem("gs_ref") || "" });
    if (r.data.token) setToken(r.data.token);
    setUser(r.data.user);
    track("signup");
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    await disablePush().catch(() => {});
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
  }, []);

  const requireAuth = useCallback((cb) => {
    if (user && user !== false) { cb && cb(); return true; }
    setAuthOpen(true);
    return false;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, authOpen, setAuthOpen, requireAuth, errMsg, badges, refreshBadges }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
